import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getMessagingEvents } from 'services/messagingHistory'
import { ChannelCountChips } from './ChannelChips'
import { failedLine, jalaliDate, openedLine, recipientsLine } from './format'

const ALL_KIND = { kind: '', title: 'همه' }

const statusOf = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status ?? null

function EventCard({
    event,
    onOpen,
}: {
    event: IMessagingEvent
    onOpen: () => void
}) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className=" mb-3 block w-full rounded-2xl border border-[#EFEFEF] bg-white p-4 text-right shadow-card active:scale-[0.99]"
        >
            <div className=" flex items-center justify-between gap-2">
                <span className=" truncate rounded-lg bg-[#E8F5EC] px-3 py-1 text-xs text-green-1">
                    {event.title}
                </span>
                <span className=" shrink-0 text-[11px] text-text-secondary">
                    {jalaliDate(event.at)}
                </span>
            </div>

            {/* dir/whitespace as on the timeline and the event page: the same
                sample text must not read one way here and another there */}
            <p
                dir="auto"
                className=" mt-2 whitespace-pre-line text-xs leading-relaxed text-text-body line-clamp-4"
            >
                {event.text}
            </p>

            <div className=" mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                <span className=" text-text-secondary">
                    {recipientsLine(event.recipients)}
                </span>
                {event.opened > 0 && (
                    <span className=" font-bold text-green-1">
                        {openedLine(event.opened)}
                    </span>
                )}
                {event.failed > 0 && (
                    <span className=" rounded-full bg-warning-soft px-2 py-0.5 text-warning-on">
                        {failedLine(event.failed)}
                    </span>
                )}
            </div>

            <ChannelCountChips counts={event.reach} />
        </button>
    )
}

/**
 * «آخرین پیام‌ها» — the same log read by event.
 *
 * One row per thing that happened, with the reach under it: who it went to, on
 * what, how much of it failed, and how much of it was opened. The text is one
 * recipient's copy — every other recipient's differs only in their own numbers.
 */
export default function EventsTab({ fundId }: { fundId: string }) {
    const navigate = useNavigate()
    const [kind, setKind] = useState('')
    const [filters, setFilters] = useState<IMessagingEventFilter[]>([])
    const [items, setItems] = useState<IMessagingEvent[]>([])
    const [page, setPage] = useState(1)
    const [hasNext, setHasNext] = useState(false)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [failed, setFailed] = useState(false)

    /**
     * Only the newest request may write — tapping from one kind chip to the
     * next is a normal way to read this list, and the slower of the two
     * answers is not the one the chips are showing.
     */
    const requestId = useRef(0)

    const loadPage = useCallback(
        async (pageNumber: number, reset: boolean) => {
            const token = ++requestId.current
            reset ? setLoading(true) : setLoadingMore(true)
            if (reset) setFailed(false)
            try {
                const data = await getMessagingEvents(fundId, {
                    kind: kind || undefined,
                    page: pageNumber,
                })
                if (token !== requestId.current) return
                const batch = data?.results ?? []
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasNext(!!data?.has_next)
                // Kept from whichever answer first carried them: a filtered
                // page may list only the kinds it matched, and chips that
                // disappear as you use them cannot be undone.
                if (data?.filters?.length)
                    setFilters((prev) =>
                        prev.length ? prev : data.filters ?? []
                    )
                if (reset) {
                    posthog.capture('announce_events_viewed', {
                        moneypool_id: Number(fundId),
                        kind,
                        event_count: batch.length,
                        has_next: !!data?.has_next,
                    })
                }
            } catch (error) {
                // reported even when superseded — the request did fail
                posthog.capture('announce_history_load_failed', {
                    screen: 'events',
                    moneypool_id: Number(fundId),
                    kind,
                    page: pageNumber,
                    status: statusOf(error),
                })
                if (token !== requestId.current) return
                if (reset) setItems([])
                setHasNext(false)
                setFailed(true)
            } finally {
                // a superseded answer must not take the spinner down from
                // under the request that replaced it
                if (token === requestId.current) {
                    setLoading(false)
                    setLoadingMore(false)
                }
            }
        },
        [fundId, kind]
    )

    useEffect(() => {
        setPage(1)
        loadPage(1, true)
    }, [loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(next, false)
    }

    const chips = [ALL_KIND, ...filters]

    return (
        <>
            {chips.length > 1 && (
                <div className=" mb-3 flex gap-2 overflow-x-auto pb-1">
                    {chips.map((chip) => {
                        const active = chip.kind === kind
                        return (
                            <button
                                key={chip.kind || 'all'}
                                type="button"
                                onClick={() => {
                                    if (active) return
                                    posthog.capture(
                                        'announce_kind_filter_changed',
                                        {
                                            moneypool_id: Number(fundId),
                                            kind: chip.kind,
                                        }
                                    )
                                    setKind(chip.kind)
                                }}
                                className={` whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                                    active
                                        ? ' bg-green-1 text-white'
                                        : ' bg-main-bg text-text-body'
                                }`}
                            >
                                {chip.title}
                            </button>
                        )
                    })}
                </div>
            )}

            {loading ? (
                <DotsLoading />
            ) : failed ? (
                <SectionError
                    message="فهرست پیام‌ها دریافت نشد"
                    onRetry={() => loadPage(1, true)}
                />
            ) : items.length === 0 ? (
                <div className=" rounded-2xl border border-[#EFEFEF] bg-white px-4 py-10 text-center text-sm text-text-secondary">
                    {kind
                        ? 'در این دسته پیامی ثبت نشده است'
                        : 'هنوز پیامی برای اعضای این صندوق ثبت نشده است'}
                </div>
            ) : (
                <>
                    {items.map((event) => (
                        <EventCard
                            key={String(event.event_id)}
                            event={event}
                            onOpen={() =>
                                navigate(
                                    `/fund/announce/${fundId}/event/${event.event_id}`
                                )
                            }
                        />
                    ))}
                    {hasNext && (
                        <button
                            type="button"
                            onClick={onLoadMore}
                            disabled={loadingMore}
                            className=" my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                        >
                            {loadingMore ? 'در حال بارگذاری...' : 'موارد بیشتر'}
                        </button>
                    )}
                </>
            )}
        </>
    )
}
